import React, { useRef } from "react";
import { Box, Typography, makeStyles, IconButton } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { OutlinedButton } from "../../../Main/Landing/components/buttons/Buttons";
import { GENESIS_CREATORS } from "../../../../pages/GenesisCollection/GenesisRosters";

const useStyles = makeStyles((theme) => ({
    featuredSection: {
        padding: '60px 5%',
        backgroundColor: '#151515',
        [theme.breakpoints.down('sm')]: {
            padding: '40px 5%',
        }
    },
    featuredHeader: {
        maxWidth: 1314,
        margin: '0 auto 40px',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 24,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
        }
    },
    featuredTitleContainer: {
        maxWidth: '600px',
    },
    featuredTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: '52px',
        fontWeight: 400,
        lineHeight: '100%',
        color: '#ffffff',
        marginBottom: '16px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '36px',
            lineHeight: '44px',
        }
    },
    featuredSubtitle: {
        fontSize: '18px',
        fontWeight: 200,
        color: '#ffffff',
        lineHeight: '27px',
    },
    desktopButton: {
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        }
    },
    mobileButtonWrapper: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '32px',
            width: '100%',
        }
    },
    featuredGridContainer: {
        maxWidth: 1314,
        margin: '0 auto',
        boxSizing: 'border-box',
        position: 'relative',
    },
    collectionGrid: {
        width: '100%',
        display: 'flex',
        overflowX: 'hidden',
        gap: 30,
        scrollBehavior: 'smooth',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        'scrollbar-width': 'none',
        [theme.breakpoints.down('sm')]: {
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            paddingBottom: theme.spacing(2),
            gap: 16,
        },
    },
    collectionCard: {
        backgroundColor: "#1B1B1B",
        overflow: 'hidden',
        position: 'relative',
        borderRadius: '12px',
        height: '527px',
        width: 'calc((100% - 60px) / 3)',
        maxWidth: 418,
        minWidth: 'calc((100% - 60px) / 3)',
        flexShrink: 0,
        boxSizing: 'border-box',
        [theme.breakpoints.down('sm')]: {
            minWidth: '280px',
            maxWidth: 'none',
            width: 'auto',
            height: '400px',
            scrollSnapAlign: 'start',
        },
        '&:hover $collectionImage': {
            transform: 'scale(1.15)',
        },
    },
    collectionImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.4s ease',
        [theme.breakpoints.down('sm')]: {
            minWidth: '300px',
        },
    },
    cardOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    cardName: {
        fontSize: '18px',
        fontWeight: 600,
        lineHeight: '27px',
        color: '#FD3C84',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '10px',
        [theme.breakpoints.down('sm')]: {
            lineHeight: '24px',
            fontSize: '16px',
            marginBottom: '8px',
        },
    },
    cardBadge: {
        fontSize: '14px',
        fontWeight: 400,
        color: '#D7A7B9',
        border: '1px solid #D7A7B9',
        borderRadius: '20px',
        padding: '4px 12px',
        marginBottom: '14px',
        textTransform: 'none',
        [theme.breakpoints.down('sm')]: {
            lineHeight: '100%',
            fontSize: '10px',
            marginBottom: '8px',
        },
    },
    cardDesc: {
        fontSize: '16px',
        fontWeight: 200,
        color: '#ffffff',
        lineHeight: '24px',
        marginBottom: '20px',
        textAlign: 'left',
        fontFamily: "'Roboto', sans-serif",
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px',
            lineHeight: '21px',
            marginBottom: '12px',
        },
    },
    cardStats: {
        display: 'flex',
        gap: '40px',
        width: '100%'
    },
    statValue: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#FFFFFF',
        lineHeight: '23.4px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px',
            lineHeight: '21px',
        },
    },
    statLabel: {
        fontSize: '16px',
        fontWeight: 200,
        color: '#747474',
        lineHeight: '24px',
        marginTop: '2px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px',
            lineHeight: '21px',
        },
    },
    navArrow: {
        border: '1px solid rgba(255,255,255,0.2)',
        color: '#ffffff',
        width: 48,
        height: 48,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 2,
        backgroundColor: 'rgba(12, 10, 9, 0.55)',
        backdropFilter: 'blur(4px)',
        '&:first-child': {
            left: -60,
        },
        '&:last-child': {
            right: -60,
        },
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        }
    }
}));

const VerifiedBadge = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M9.999 1.547L7.319 0L5.833 2.575H2.716V5.752L0 7.32L1.547 10L0 12.6795L2.716 14.248V17.2005H5.7035L7.319 20L9.999 18.453L12.679 20L14.295 17.2H17.376V14.195L20 12.6795L18.4525 10L20 7.3205L17.376 5.8055V2.5755H14.1665L12.679 0L9.999 1.547ZM13.7065 7.1595L14.8335 8.303L9.1185 14.053L5.701 10.588L6.823 9.459L9.1165 11.7495L13.7065 7.1595Z" fill="#FE5A97" />
    </svg>
);

const FEATURED_CREATORS = GENESIS_CREATORS;


export const VaultFeaturedSection: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const scrollRef = useRef<HTMLDivElement>(null);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = scrollRef.current.clientWidth + 30; // Container width + gap
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <Box className={classes.featuredSection}>
            <Box className={classes.featuredHeader}>
                <Box className={classes.featuredTitleContainer}>
                    <Typography variant="h2" className={classes.featuredTitle}>
                        Featured Creators
                    </Typography>
                    <Typography className={classes.featuredSubtitle}>
                        A rotating spotlight on the creators shaping The Vault's inner circle.
                    </Typography>
                </Box>
                <Box className={classes.desktopButton}>
                    <OutlinedButton text="View Genesis Collection" onClick={() => history.push('/genesis-collection')} />
                </Box>
            </Box>

            <Box className={classes.featuredGridContainer}>
                <IconButton className={classes.navArrow} onClick={() => scroll('left')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </IconButton>

                <div className={classes.collectionGrid} ref={scrollRef}>
                    {FEATURED_CREATORS.map((creator, index) => (
                        <Box key={index} className={classes.collectionCard}>
                            <img
                                src={creator.image}
                                alt={creator.name}
                                className={classes.collectionImage}
                            />
                            <Box className={classes.cardOverlay}>
                                <Typography className={classes.cardName}>
                                    {creator.name} <VerifiedBadge />
                                </Typography>
                                <span className={classes.cardBadge}>{creator.badge}</span>
                                <Typography className={classes.cardDesc}>
                                    {creator.description}
                                </Typography>
                                <Box className={classes.cardStats}>
                                    <Box>
                                        <Typography className={classes.statValue}>{creator.posts}</Typography>
                                        <Typography className={classes.statLabel}>Posts</Typography>
                                    </Box>
                                    <Box>
                                        <Typography className={classes.statValue}>{creator.subscribers}</Typography>
                                        <Typography className={classes.statLabel}>Subscribers</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </div>

                <IconButton className={classes.navArrow} onClick={() => scroll('right')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </IconButton>
            </Box>

            <Box className={classes.mobileButtonWrapper}>
                <OutlinedButton text="View Genesis Collection" onClick={() => history.push('/genesis-collection')} />
            </Box>
        </Box>
    );
};
